import {
  Anchor,
  Avatar,
  Button,
  UtilityButton,
  Tooltip,
  DropdownMenu,
  DropdownMenuText,
  DropdownMenuButton,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuList,
  Heading,
  Inline,
  InlineItem,
  Modal,
  Frame,
  Container,
  Header,
  Footer,
  Content,
  Separator,
  Toolbar,
  ToolbarItem,
} from '@kinesis/bungle';
import { shortcuts } from '@/shortcuts';
import { pathwayPath, baselinePath, gpcPath } from '@/links';
import { useState, useCallback } from 'react';
import styled from 'styled-components';
import type { Session, Flag, Inventory } from '@/types';
import { Link } from 'react-router-dom';
import { Layout } from './layout';
import { Logo } from '../logo';
import { noop } from 'lodash/fp';
import { UnlockFilledIcon } from '@/components/unlock-filled-icon';
import { FeatureFlagCard } from '@/components/feature-flags';
import { EvaluateIcon } from './evaluate-icon';
import { AssembleIcon } from './assemble-icon';
import { DiscloseIcon } from './disclose-icon';
import { useNavSelection } from '@/hooks/use-nav-selection';
import { ToolbarLeft } from './toolbar-left';
import { ToolbarRight } from './toolbar-right';
import { useResponsive } from '@/hooks/use-responsive';

const StyledSeparator = styled(Separator)({
  opacity: '15%',
});

const PrimaryNavigation = (props: PrimaryNavigationProps) => {
  const {
    enableFlags = false,
    enableBaseline = false,
    enableGpc = false,
    onLogout = noop,
    session,
    flags,
    onToggleFlag,
    onOpenSwitcher = noop,
    inventoryId,
    inventory,
  } = props;
  const navSelection = useNavSelection();
  const { isTabletLandscapeAndUp } = useResponsive();

  const [flagModal, setFlagModal] = useState(false);
  const onOpenFlagModal = useCallback(() => {
    setFlagModal(true);
  }, [setFlagModal]);
  const onCloseFlagModal = useCallback(() => {
    setFlagModal(false);
  }, [setFlagModal]);

  return (
    <Content>
      {flagModal && (
        <Modal
          aria-label='Feature flags'
          onClose={onCloseFlagModal}
          width={496}
          height={600}
          minHeight={320}
        >
          <Container>
            <Header border>
              <Toolbar>
                <ToolbarItem sizing='fill-container'>
                  <Heading as='h2' size='large'>
                    Feature flags
                  </Heading>
                </ToolbarItem>
              </Toolbar>
            </Header>
            <Frame>
              <FeatureFlagCard
                featureFlagList={flags}
                onToggleFlag={onToggleFlag}
              />
            </Frame>

            <Footer border>
              <Toolbar>
                <ToolbarItem>
                  <Button onClick={onCloseFlagModal}>Done</Button>
                </ToolbarItem>
              </Toolbar>
            </Footer>
          </Container>
        </Modal>
      )}
      <Layout>
        <ToolbarLeft>
          <Inline space='medium'>
            <InlineItem>
              <Anchor onClick={onOpenSwitcher}>
                <Logo />
              </Anchor>
            </InlineItem>
            <InlineItem>
              <Inline space='medium'>
                <InlineItem>
                  <Tooltip title={'Pathway'} placement='bottom' offset={8}>
                    <UtilityButton
                      icon={<EvaluateIcon />}
                      displayMode='dark'
                      as={Link}
                      to={pathwayPath(inventoryId)}
                      variant={
                        navSelection === 'pathway' ? 'accent' : 'default'
                      }
                    >
                      {isTabletLandscapeAndUp && 'Pathway'}
                    </UtilityButton>
                  </Tooltip>
                </InlineItem>
                {enableBaseline && (
                  <InlineItem>
                    <Tooltip title={'Baseline'} placement='bottom' offset={8}>
                      <UtilityButton
                        icon={<AssembleIcon />}
                        displayMode='dark'
                        as={Link}
                        to={baselinePath(inventoryId)}
                        variant={
                          navSelection === 'baseline' ? 'accent' : 'default'
                        }
                      >
                        {isTabletLandscapeAndUp && 'Baseline'}
                      </UtilityButton>
                    </Tooltip>
                  </InlineItem>
                )}
                {enableGpc && (
                  <InlineItem>
                    <Tooltip title={'Reporting'} placement='bottom' offset={8}>
                      <UtilityButton
                        icon={<DiscloseIcon />}
                        displayMode='dark'
                        as={Link}
                        to={gpcPath(inventoryId)}
                        variant={navSelection === 'gpc' ? 'accent' : 'default'}
                      >
                        {isTabletLandscapeAndUp && 'Reporting'}
                      </UtilityButton>
                    </Tooltip>
                  </InlineItem>
                )}
              </Inline>
            </InlineItem>
          </Inline>
        </ToolbarLeft>
        <ToolbarRight>
          <Inline space='medium'>
            {inventory && (
              <>
                <InlineItem>
                  <Tooltip
                    title={inventory?.name}
                    placement='bottom'
                    offset={8}
                  >
                    <UtilityButton
                      variant='accent'
                      displayMode='dark'
                      as={Link}
                    >
                      <Inline space='xsmall'>
                        <InlineItem>
                          <Avatar
                            image={inventory?.owner?.avatar}
                            magnitude='xsmall'
                            variant='organisation'
                          >
                            {inventory?.owner?.label?.slice(0, 1)}
                          </Avatar>
                        </InlineItem>
                        {isTabletLandscapeAndUp && (
                          <InlineItem>
                            <div
                              style={{
                                paddingLeft: 2,
                              }}
                            >
                              {inventory?.name}
                            </div>
                          </InlineItem>
                        )}
                      </Inline>
                    </UtilityButton>
                  </Tooltip>
                </InlineItem>
                <StyledSeparator />
              </>
            )}
            <InlineItem>
              <Inline space='small'>
                <InlineItem>
                  <Tooltip
                    title={`Switch to… (${shortcuts.SWITCHER.mnemonic})`}
                    placement='bottom'
                    offset={8}
                  >
                    <UtilityButton
                      icon='switcher'
                      displayMode='dark'
                      onClick={onOpenSwitcher}
                      keyboardShortcut={shortcuts.SWITCHER.handler}
                    >
                      {isTabletLandscapeAndUp && 'Switch to…'}
                    </UtilityButton>
                  </Tooltip>
                </InlineItem>
                {enableFlags && (
                  <InlineItem>
                    <Tooltip
                      title='Feature flags'
                      placement='bottom'
                      offset={8}
                    >
                      <UtilityButton
                        icon='flag'
                        displayMode='dark'
                        onClick={onOpenFlagModal}
                      />
                    </Tooltip>
                  </InlineItem>
                )}
                <InlineItem>
                  <Tooltip
                    title='Access controls'
                    placement='bottom'
                    offset={8}
                  >
                    <UtilityButton
                      icon={<UnlockFilledIcon magnitude='medium' />}
                      displayMode='dark'
                      as={Link}
                      to={`/communities/${inventoryId}/access`}
                      variant={navSelection === 'access' ? 'accent' : 'default'}
                    />
                  </Tooltip>
                </InlineItem>
                <InlineItem>
                  <Tooltip title='Settings' placement='bottom' offset={8}>
                    <UtilityButton
                      as='a'
                      href='/settings'
                      target='_blank'
                      icon='setting-filled'
                      displayMode='dark'
                    />
                  </Tooltip>
                </InlineItem>
                <InlineItem>
                  <Tooltip
                    title='Help and resources'
                    placement='bottom'
                    offset={8}
                  >
                    <UtilityButton
                      as='a'
                      href='https://help.kinesis.org/'
                      target='_blank'
                      icon='question-circle'
                      displayMode='dark'
                    />
                  </Tooltip>
                </InlineItem>
                <InlineItem>
                  <DropdownMenu justify='end' placement='bottom'>
                    <DropdownMenuButton
                      as={Avatar}
                      displayMode='dark'
                      interactive
                      variant='individual'
                      magnitude='small'
                    >
                      {session?.initials}
                    </DropdownMenuButton>
                    <DropdownMenuList offset={8}>
                      <DropdownMenuText>
                        <Heading as='h3' size='medium'>
                          {session?.firstName} {session?.lastName}
                        </Heading>
                      </DropdownMenuText>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={onLogout}>
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuList>
                  </DropdownMenu>
                </InlineItem>
              </Inline>
            </InlineItem>
          </Inline>
        </ToolbarRight>
      </Layout>
    </Content>
  );
};

export type PrimaryNavigationProps = {
  inventoryId: number;
  enableFlags?: boolean;
  enableBaseline?: boolean;
  enableGpc?: boolean;
  flags: Flag[];
  onLogout?: () => void;
  onToggleFlag?: (name: string) => void;
  session?: Session;
  animation?: boolean;
  onOpenSwitcher?: () => void;
  inventory?: Inventory;
};

export { PrimaryNavigation };
